import { useTranslation } from 'react-i18next'
import { CONTENT_LANGUAGE_NAMES } from '../../services/translationService'

/**
 * Honest inline notice shown where DB-served content is displayed, when the requested language
 * has no translation yet. Example: a visitor viewing the site in हिन्दी opens an article that has
 * no Hindi translation — the API returns the English content with `matchedLanguage="en"`, and this
 * notice says "This content is not yet available in हिन्दी — showing English."
 *
 * When `matchedLanguage` equals the active UI language (a translation exists), this renders nothing.
 * When `matchedLanguage` is absent (older responses without the field), it also renders nothing —
 * the notice is opt-in per content block.
 */
export function TranslatedContentNotice({ matchedLanguage }: { matchedLanguage?: string | null }) {
  const { t, i18n } = useTranslation()
  if (!matchedLanguage) return null
  const requested = i18n.language?.split('-')[0] || 'en'
  if (matchedLanguage === requested) return null

  const requestedName = CONTENT_LANGUAGE_NAMES[requested] || requested
  const shownName = CONTENT_LANGUAGE_NAMES[matchedLanguage] || matchedLanguage

  return (
    <div
      role="status"
      className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
    >
      <span aria-hidden className="mt-0.5">ℹ️</span>
      <span>
        {t('common.translationFallback', {
          requested: requestedName,
          shown: shownName,
          defaultValue:
            'This content is not yet available in {{requested}} — showing {{shown}}.',
        })}
      </span>
    </div>
  )
}